/**
 * XP and levels. CONFIGURABLE curve: quadratic by default (each level costs
 * more), or explicit thresholds. Pure and deterministic.
 */

export interface XpCurve {
  /** XP required to reach level `n` (n≥1, level 1 = 0 XP). */
  xpForLevel(level: number): number;
}

/** Quadratic curve: xpForLevel(n) = base * (n-1)^2. */
export function quadraticCurve(base = 100): XpCurve {
  return {
    xpForLevel(level: number): number {
      const l = Math.max(1, Math.floor(level));
      return base * (l - 1) * (l - 1);
    },
  };
}

/** Curve with explicit thresholds (thresholds[0] = level 1 = 0). */
export function thresholdCurve(thresholds: readonly number[]): XpCurve {
  return {
    xpForLevel(level: number): number {
      const l = Math.max(1, Math.floor(level));
      if (l - 1 < thresholds.length) {
        return thresholds[l - 1] ?? 0;
      }
      // Extrapolates using the last known step.
      const last = thresholds.at(-1) ?? 0;
      const prev = thresholds.at(-2) ?? 0;
      const step = last - prev;
      return last + step * (l - thresholds.length);
    },
  };
}

export const DEFAULT_CURVE: XpCurve = quadraticCurve(100);

/** Level reached with `xp` points (level 1 = 0 XP). */
export function levelForXp(xp: number, curve: XpCurve = DEFAULT_CURVE): number {
  const x = Math.max(0, xp);
  let level = 1;
  // Rises while the next level's threshold is met. Bounded for safety.
  while (level < 10_000 && curve.xpForLevel(level + 1) <= x) {
    level += 1;
  }
  return level;
}

export interface LevelProgress {
  level: number;
  /** XP within the current level. */
  intoLevel: number;
  /** Total XP required from the current level to the next. */
  levelSpan: number;
  /** 0..1 progress toward the next level. */
  ratio: number;
  xpToNext: number;
}

export function levelProgress(xp: number, curve: XpCurve = DEFAULT_CURVE): LevelProgress {
  const level = levelForXp(xp, curve);
  const floor = curve.xpForLevel(level);
  const ceil = curve.xpForLevel(level + 1);
  const span = Math.max(1, ceil - floor);
  const into = Math.max(0, xp - floor);
  return {
    level,
    intoLevel: into,
    levelSpan: span,
    ratio: Math.max(0, Math.min(1, into / span)),
    xpToNext: Math.max(0, ceil - xp),
  };
}
